import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  Plus, Edit, Trash2, Play, Clock, Send, Check, X,
  CornerDownRight, RotateCcw, FileText, MessageSquare
} from 'lucide-react';

const taskStatuses = [
  { id: 'not_started', label: 'Not Started', color: 'bg-muted text-muted-foreground' },
  { id: 'assigned', label: 'Assigned', color: 'bg-cyan-500/20 text-cyan-600' },
  { id: 'in_progress', label: 'In Progress', color: 'bg-blue-500/20 text-blue-600' },
  { id: 'on_hold', label: 'On Hold', color: 'bg-yellow-500/20 text-yellow-600' },
  { id: 'under_review', label: 'Under Review', color: 'bg-purple-500/20 text-purple-600' },
  { id: 'overdue', label: 'Overdue', color: 'bg-red-500/20 text-red-600' },
  { id: 'completed', label: 'Completed', color: 'bg-green-500/20 text-green-600' }
];

const selectableStatuses = taskStatuses.filter(s => s.id !== 'overdue');

const priorities = [
  { id: 'low', label: 'Low', color: 'bg-slate-500/20 text-slate-600' },
  { id: 'medium', label: 'Medium', color: 'bg-blue-500/20 text-blue-600' },
  { id: 'high', label: 'High', color: 'bg-orange-500/20 text-orange-600' },
  { id: 'urgent', label: 'Overdue', color: 'bg-red-500/20 text-red-600' }
];

const formatTime = (minutes) => {
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
};

export const CreateTaskPageDialog = ({
  open, onOpenChange, form, setForm, onSubmit,
  projects, tasks, teamMembers,
  newSubtaskTitle, setNewSubtaskTitle
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Create New Task</DialogTitle>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
          <input
            type="checkbox" id="is_internal" checked={form.is_internal}
            onChange={(e) => setForm({ ...form, is_internal: e.target.checked, project_id: e.target.checked ? '' : form.project_id, parent_task_id: '' })}
            className="h-4 w-4 rounded border-gray-300"
          />
          <label htmlFor="is_internal" className="flex-1 cursor-pointer">
            <span className="font-medium">Internal Task</span>
            <p className="text-xs text-muted-foreground">Not billable to any project - for internal business operations</p>
          </label>
        </div>

        {!form.is_internal && (
          <div>
            <label className="text-sm font-medium mb-2 block">Project *</label>
            <Select value={form.project_id} onValueChange={(value) => setForm({ ...form, project_id: value, parent_task_id: '' })} required={!form.is_internal}>
              <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>
                {projects.map((p) => (<SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        )}

        {!form.is_internal && form.project_id && (
          <div>
            <label className="text-sm font-medium mb-2 block">Nest Under Task (Optional)</label>
            <Select value={form.parent_task_id || '__none__'} onValueChange={(value) => setForm({ ...form, parent_task_id: value === '__none__' ? '' : value })}>
              <SelectTrigger><SelectValue placeholder="Create as parent task" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Create as parent task</SelectItem>
                {tasks.filter(t => t.project_id === form.project_id && !t.parent_task_id).map((t) => (
                  <SelectItem key={t.task_id} value={t.task_id}>
                    <div className="flex items-center gap-2"><CornerDownRight className="h-3 w-3" />{t.title}</div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">Select a parent task to create this as a subtask</p>
          </div>
        )}

        <div>
          <label className="text-sm font-medium mb-2 block">Task Title *</label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Enter task title" required />
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Description</label>
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Task description" rows={3} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Priority</label>
            <Select value={form.priority} onValueChange={(value) => setForm({ ...form, priority: value })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{priorities.map((p) => (<SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Assign To</label>
            <Select value={form.assigned_to || '__none__'} onValueChange={(value) => setForm({ ...form, assigned_to: value === '__none__' ? '' : value })}>
              <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {teamMembers.map((m) => (<SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Start Date</label>
            <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">End Date</label>
            <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Initial Comment / Instructions (Optional)</label>
          <Textarea value={form.initial_comment} onChange={(e) => setForm({ ...form, initial_comment: e.target.value })} placeholder="Add any specific instructions or comments for this task..." rows={2} />
          <p className="text-xs text-muted-foreground mt-1">This comment will be added to the task upon creation</p>
        </div>

        {!form.parent_task_id && (
          <div className="border rounded-lg p-4 bg-muted/30">
            <label className="text-sm font-medium mb-3 block">Auto-Create Subtasks (Optional)</label>
            <div className="space-y-2">
              {form.subtasks_to_create.map((st, idx) => (
                <div key={`subtask-${st.title}-${idx}`} className="flex items-center gap-2 p-2 bg-background rounded border">
                  <CornerDownRight className="h-4 w-4 text-muted-foreground" />
                  <span className="flex-1 text-sm">{st.title}</span>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setForm({ ...form, subtasks_to_create: form.subtasks_to_create.filter((_, i) => i !== idx) })}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input
                  value={newSubtaskTitle} onChange={(e) => setNewSubtaskTitle(e.target.value)} placeholder="Enter subtask title"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (newSubtaskTitle.trim()) { setForm({ ...form, subtasks_to_create: [...form.subtasks_to_create, { title: newSubtaskTitle.trim() }] }); setNewSubtaskTitle(''); } } }}
                />
                <Button type="button" variant="outline" onClick={() => { if (newSubtaskTitle.trim()) { setForm({ ...form, subtasks_to_create: [...form.subtasks_to_create, { title: newSubtaskTitle.trim() }] }); setNewSubtaskTitle(''); } }}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Press Enter or click + to add subtasks</p>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => { onOpenChange(false); setNewSubtaskTitle(''); }}>Cancel</Button>
          <Button type="submit">{form.subtasks_to_create.length > 0 ? `Create Task with ${form.subtasks_to_create.length} Subtasks` : 'Create Task'}</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);

export const CreateSubtaskPageDialog = ({ open, onOpenChange, form, setForm, onSubmit, parentTitle, teamMembers }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle>Create Subtask for "{parentTitle}"</DialogTitle></DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Subtask Title</label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Enter subtask title" required />
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Description</label>
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Subtask description" rows={2} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Priority</label>
            <Select value={form.priority} onValueChange={(value) => setForm({ ...form, priority: value })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{priorities.map((p) => (<SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Assign To</label>
            <Select value={form.assigned_to} onValueChange={(value) => setForm({ ...form, assigned_to: value })}>
              <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>{teamMembers.map((m) => (<SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Initial Comment / Instructions (Optional)</label>
          <Textarea value={form.initial_comment} onChange={(e) => setForm({ ...form, initial_comment: e.target.value })} placeholder="Add any specific instructions or comments..." rows={2} />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit">Create Subtask</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);

export const EditTaskPageDialog = ({ open, onOpenChange, form, setForm, onSubmit, teamMembers }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl">
      <DialogHeader><DialogTitle>Edit Task</DialogTitle></DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Task Title</label>
          <Input value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Description</label>
          <Textarea value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Priority</label>
            <Select value={form.priority || 'medium'} onValueChange={(value) => setForm({ ...form, priority: value })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{priorities.map((p) => (<SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Assign To</label>
            <Select value={form.assigned_to || ''} onValueChange={(value) => setForm({ ...form, assigned_to: value })}>
              <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>{teamMembers.map((m) => (<SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Start Date</label>
            <Input type="date" value={form.start_date || ''} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">End Date</label>
            <Input type="date" value={form.end_date || ''} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Reviewer</label>
          <Select value={form.reviewer_id || ''} onValueChange={(value) => setForm({ ...form, reviewer_id: value })}>
            <SelectTrigger><SelectValue placeholder="No reviewer" /></SelectTrigger>
            <SelectContent>
              {teamMembers.filter(m => ['admin', 'manager', 'team_lead'].includes(m.role)).map((m) => (
                <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit">Save Changes</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);

export const TaskDetailPageDialog = ({
  open, onOpenChange, task, user, teamMembers, projects,
  newComment, setNewComment, editingComment, setEditingComment, editCommentText, setEditCommentText,
  onAddComment, onEditComment, onDeleteComment,
  onStatusChange, onEdit, onDelete, onStartTimer,
  onSendForReview, onApproveReview, onReturnToOwner,
  onCreateSubtask
}) => {
  const getStatusBadge = (status) => {
    const info = taskStatuses.find(s => s.id === status) || taskStatuses[0];
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${info.color}`}>{info.label}</span>;
  };
  const getPriorityBadge = (priority) => {
    const info = priorities.find(p => p.id === priority) || priorities[1];
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${info.color}`}>{info.label}</span>;
  };
  const isReviewer = task?.reviewer_id === user?.user_id;
  const taskProject = task?.project_id ? (projects || []).find(p => p.project_id === task.project_id) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {task?.title}
            {task && getStatusBadge(task.status)}
          </DialogTitle>
        </DialogHeader>
        {task && (
          <div className="space-y-6">
            {/* Project & Client Info */}
            {taskProject && (
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border" data-testid="task-project-info">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-primary/70 bg-primary/10 px-2 py-0.5 rounded font-medium">{taskProject.project_code || 'No Code'}</span>
                    <span className="font-medium text-sm">{taskProject.name}</span>
                  </div>
                  {(taskProject.client_name || taskProject.company_name) && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {taskProject.company_name && <span>{taskProject.company_name}</span>}
                      {taskProject.company_name && taskProject.client_name && <span> | </span>}
                      {taskProject.client_name && <span>{taskProject.client_name}</span>}
                    </p>
                  )}
                </div>
              </div>
            )}
            {!task.project_id && task.is_internal && (
              <div className="flex items-center gap-2 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                <span className="text-xs font-medium text-amber-700 bg-amber-500/20 px-2 py-0.5 rounded">Internal Task</span>
              </div>
            )}
            {task.is_fee_linked && (
              <div className="flex items-center gap-2 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20" data-testid="fee-linked-banner">
                <FileText className="h-4 w-4 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-700">Linked to Fee Structure deliverable</span>
                <span className="text-xs text-emerald-600/70 ml-auto">Status syncs automatically with Fee Structure</span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div><p className="text-sm text-muted-foreground mb-1">Priority</p>{getPriorityBadge(task.priority)}</div>
              <div><p className="text-sm text-muted-foreground mb-1">Assigned To</p><p className="font-medium">{teamMembers.find(m => m.user_id === task.assigned_to)?.name || 'Unassigned'}</p></div>
              {task.start_date && <div><p className="text-sm text-muted-foreground mb-1">Start Date</p><p className="font-medium">{task.start_date}</p></div>}
              {task.end_date && <div><p className="text-sm text-muted-foreground mb-1">Due Date</p><p className="font-medium">{task.end_date}</p></div>}
              {task.total_tracked_time > 0 && <div><p className="text-sm text-muted-foreground mb-1">Time Tracked</p><p className="font-medium flex items-center gap-1"><Clock className="h-4 w-4" />{formatTime(task.total_tracked_time)}</p></div>}
            </div>
            {task.description && <div><p className="text-sm text-muted-foreground mb-2">Description</p><p className="text-sm">{task.description}</p></div>}
            {task.review_notes && (
              <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                <p className="text-sm font-medium text-yellow-600 mb-1">Reviewer Notes</p>
                <p className="text-sm">{task.review_notes}</p>
              </div>
            )}

            {!task.parent_task_id && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium">Subtasks ({task.subtasks?.length || 0})</p>
                  <Button size="sm" variant="outline" onClick={onCreateSubtask}><Plus className="mr-1 h-3 w-3" />Add Subtask</Button>
                </div>
                {task.subtask_details?.length > 0 ? (
                  <div className="space-y-2">
                    {task.subtask_details.map((st) => (
                      <div key={st.task_id} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                        <div className="flex items-center gap-2">
                          <CornerDownRight className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{st.title}</span>
                          {getStatusBadge(st.status)}
                        </div>
                        <Select value={st.status} onValueChange={(v) => onStatusChange(st.task_id, v)}>
                          <SelectTrigger className="w-28 h-7 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>{selectableStatuses.map((s) => (<SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                ) : (<p className="text-sm text-muted-foreground text-center py-2">No subtasks</p>)}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => onEdit(task)}><Edit className="mr-2 h-4 w-4" />Edit</Button>
              <Button variant="outline" onClick={() => onStartTimer(task.task_id)}><Play className="mr-2 h-4 w-4" />Start Timer</Button>
              {task.status === 'in_progress' && task.assigned_to === user?.user_id && (
                <Select onValueChange={(reviewerId) => onSendForReview(task.task_id, reviewerId)}>
                  <SelectTrigger className="w-48"><div className="flex items-center gap-2"><Send className="h-4 w-4" /><span>Send for Review</span></div></SelectTrigger>
                  <SelectContent>
                    {teamMembers.filter(m => ['admin', 'manager', 'team_lead'].includes(m.role)).map((m) => (
                      <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {task.status === 'under_review' && isReviewer && (
                <>
                  <Button onClick={() => onApproveReview(task.task_id)} className="bg-green-500 hover:bg-green-600"><Check className="mr-2 h-4 w-4" />Approve & Continue</Button>
                  <Button variant="outline" onClick={() => onReturnToOwner(task.task_id)}><RotateCcw className="mr-2 h-4 w-4" />Return to Owner</Button>
                </>
              )}
              <Button variant="destructive" onClick={() => onDelete(task.task_id)}><Trash2 className="mr-2 h-4 w-4" />Delete</Button>
            </div>

            <div>
              <p className="text-sm font-medium mb-3">Comments ({task.comments?.length || 0})</p>
              <div className="space-y-3 max-h-64 overflow-y-auto mb-3">
                {task.comments?.map((comment) => (
                  <div key={comment.comment_id || comment.timestamp} className="p-3 bg-muted rounded-lg group">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">{comment.user_name}</p>
                        {comment.edited && <span className="text-xs text-muted-foreground">(edited)</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-muted-foreground">{new Date(comment.timestamp).toLocaleString()}</p>
                        {comment.user_id === user?.user_id && !comment.is_system && (
                          <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                            <button className="p-1 hover:bg-background rounded" onClick={() => { setEditingComment(comment.comment_id); setEditCommentText(comment.comment); }}><Edit className="h-3 w-3" /></button>
                            <button className="p-1 hover:bg-background rounded text-destructive" onClick={() => onDeleteComment(comment.comment_id)}><Trash2 className="h-3 w-3" /></button>
                          </div>
                        )}
                      </div>
                    </div>
                    {editingComment === comment.comment_id ? (
                      <div className="flex gap-2 mt-2">
                        <Input value={editCommentText} onChange={(e) => setEditCommentText(e.target.value)} className="flex-1" autoFocus />
                        <Button size="sm" onClick={() => onEditComment(comment.comment_id)}><Check className="h-3 w-3" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingComment(null)}><X className="h-3 w-3" /></Button>
                      </div>
                    ) : (<p className="text-sm">{comment.comment}</p>)}
                  </div>
                ))}
                {(!task.comments || task.comments.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-4">No comments yet</p>
                )}
              </div>
              <div className="flex gap-2">
                <Input value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Add a comment..." onKeyPress={(e) => e.key === 'Enter' && onAddComment()} />
                <Button onClick={onAddComment} disabled={!newComment.trim()}><Send className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
